import { useFormikContext } from 'formik';
import { useMemo } from 'react';

import type { Asset2CampaignGqlView } from '../../../../../apis/graphql';
import type { Nullable } from '../../../../../models';
import { convertExtendedAssetToGQLView } from '../../../../../utils/assets';
import type { AdFormValues } from '../../../adFormik';
import { useCreativeData } from '../../hooks/useCreativeData';

export type UseAdSelectorSlateAssets = {
  loadingCreativeData: boolean;
  assets: Nullable<Asset2CampaignGqlView>[];
  selectedAsset?: Asset2CampaignGqlView | null;
};

export type UseAdSelectorSlateAssetsProps = {
  selectedTabIndex: number;
};

export const useAdSelectorSlateAssets = ({
  selectedTabIndex,
}: UseAdSelectorSlateAssetsProps): UseAdSelectorSlateAssets => {
  const {
    values: { creative, lineItem },
  } = useFormikContext<AdFormValues>();

  const {
    data: { assets },
    loading: loadingCreativeData,
  } = useCreativeData(creative, lineItem?.displayPublisherTarget);

  // merge assets fetched in useCreativeData and assets from option list
  const sortedAssets = useMemo((): Nullable<Asset2CampaignGqlView>[] => {
    const resultAssetList: Nullable<Asset2CampaignGqlView>[] = [];

    const logoAsset = assets.find(({ id }) => id === creative.logo?.id);
    resultAssetList.push(convertExtendedAssetToGQLView(logoAsset));

    if (creative.optionList) {
      creative.optionList.forEach((option) => {
        const foundFetchedAsset = assets.find(({ id }) => option.thumbnailId === id);

        if (foundFetchedAsset) {
          resultAssetList.push(convertExtendedAssetToGQLView(foundFetchedAsset));
        } else if (option.thumbnail?.id) {
          resultAssetList.push(option.thumbnail);
        } else {
          resultAssetList.push(null);
        }
      });
    }

    return resultAssetList;
  }, [assets, creative.logo?.id, creative.optionList]);

  return {
    loadingCreativeData,
    assets: sortedAssets,
    selectedAsset: sortedAssets[selectedTabIndex],
  };
};
